import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/Card';
import { clientPortalAPI } from '../../api';
import { FileText, ExternalLink, Download, Loader2, CreditCard } from 'lucide-react';
import { format } from 'date-fns';

const formatCurrency = (n) =>
  typeof n === 'number' && !Number.isNaN(n)
    ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n)
    : '—';

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Calendar day after due date => overdue (matches tenant rule: unpaid + past due). */
const isPastDueDate = (dueDateStr) => {
  if (!dueDateStr) return false;
  return startOfDay(new Date()) > startOfDay(new Date(dueDateStr));
};

/**
 * Client-facing status: "Sent" → "Unpaid"; past due with money owed → "Overdue".
 */
const getClientInvoiceDisplay = (inv) => {
  const status = inv.status || '';
  const balance = parseFloat(inv.balanceDue) || 0;

  if (status === 'draft') {
    return { key: 'draft', label: 'Draft' };
  }
  if (status === 'paid-in-full') {
    return { key: 'paid', label: 'Paid' };
  }
  if (balance <= 0) {
    return { key: 'paid', label: 'Paid' };
  }

  if (isPastDueDate(inv.dueDate)) {
    return { key: 'overdue', label: 'Overdue' };
  }
  if (status === 'part-paid') {
    return { key: 'part-paid', label: 'Part paid' };
  }
  return { key: 'unpaid', label: 'Unpaid' };
};

const statusBadgeClass = (displayKey) => {
  const base = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ';
  switch (displayKey) {
    case 'draft':
      return base + 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    case 'unpaid':
    case 'part-paid':
      return base + 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200';
    case 'paid':
      return base + 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200';
    case 'overdue':
      return base + 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200';
    default:
      return base + 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  }
};

export default function ClientInvoices() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['client-portal', 'my-invoices'],
    queryFn: () => clientPortalAPI.getMyInvoices(),
  });
  const invoices = Array.isArray(data) ? data : (data?.invoices ?? []);
  const payEnabled = data?.payEnabled !== false;

  const canPay = (inv) =>
    payEnabled &&
    (parseFloat(inv.balanceDue) || 0) > 0 &&
    ['sent', 'part-paid', 'overdue'].includes(inv.status);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
            Your Invoices
          </h1>
          <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
            View and download your invoices
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="bg-white dark:bg-gray-800">
          <CardContent className="p-0">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--ds-text-muted)]" />
              </div>
            )}
            {error && (
              <div className="p-6 text-center text-red-600 dark:text-red-400">
                Failed to load invoices. Please try again later.
              </div>
            )}
            {!isLoading && !error && invoices.length === 0 && (
              <div className="p-12 text-center text-[var(--ds-text-secondary)]">
                <FileText className="mx-auto h-12 w-12 text-[var(--ds-text-muted)] mb-3" />
                <p className="text-body">You have no invoices yet.</p>
              </div>
            )}
            {!isLoading && !error && invoices.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Invoice
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Reference
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Issued
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Due
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Status
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Total
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300">
                        Balance
                      </th>
                      <th className="px-4 py-3 text-meta font-medium text-gray-700 dark:text-gray-300 text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {invoices.map((inv) => {
                      const disp = getClientInvoiceDisplay(inv);
                      return (
                        <tr
                          key={inv.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/30"
                        >
                          <td className="px-4 py-3 text-body font-medium text-[var(--ds-text-primary)]">
                            {inv.invoiceNum}
                          </td>
                          <td className="px-4 py-3 text-body text-[var(--ds-text-secondary)]">
                            {inv.reference || '—'}
                          </td>
                          <td className="px-4 py-3 text-body text-[var(--ds-text-secondary)]">
                            {inv.dateIssued
                              ? format(new Date(inv.dateIssued), 'dd MMM yyyy')
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-body text-[var(--ds-text-secondary)]">
                            {inv.dueDate
                              ? format(new Date(inv.dueDate), 'dd MMM yyyy')
                              : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={statusBadgeClass(disp.key)}>{disp.label}</span>
                          </td>
                          <td className="px-4 py-3 text-body text-[var(--ds-text-primary)]">
                            {formatCurrency(inv.total)}
                          </td>
                          <td className="px-4 py-3 text-body text-[var(--ds-text-primary)]">
                            {formatCurrency(inv.balanceDue)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <a
                              href={`${baseUrl}/portal/invoice/${inv.viewToken}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              <ExternalLink className="h-4 w-4" />
                              View
                            </a>
                            <a
                              href={`${baseUrl}/api/portal/invoice/${inv.viewToken}/pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:underline"
                            >
                              <Download className="h-4 w-4" />
                              PDF
                            </a>
                            {canPay(inv) && (
                              <Link
                                to={`/client/pay?token=${encodeURIComponent(inv.viewToken)}`}
                                className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                <CreditCard className="h-4 w-4" />
                                Pay
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
